import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, X, ChevronDown, RotateCcw, CalendarCheck, Check, ExternalLink, Loader2, Sparkles } from "lucide-react";
import { usePrefersReducedMotion } from "../components/journey/hooks";
import { buildWattzapOSUrl, OS_REDIRECT_NOTICE, OS_FALLBACK_TEXT, isEmbeddedPreview } from "../lib/wattzapOS";
import { useT } from "../i18n/LanguageContext";
import WattBotCharacter from "./WattBotCharacter";
import { QUESTIONS, INDUSTRY_ACK, PRODUCT_META, recommend, track } from "./rules";

const SKEY = "wattzap_concierge_v1";
const load = () => { try { return JSON.parse(sessionStorage.getItem(SKEY)) || {}; } catch { return {}; } };
const save = (patch) => { try { sessionStorage.setItem(SKEY, JSON.stringify({ ...load(), ...patch })); } catch { /* ignore */ } };

const CARD = "flex items-center gap-3 w-full text-left border rounded-xl px-4 py-3 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00D4FF]";

const conciergeParams = (a = {}) => ({
  src: "concierge",
  facility: a.industry || "",
  bill: a.bill || "",
  investigate: (a.problem || []).join(","),
  state: a.state || "",
});

const goToOS = (params) => {
  const url = buildWattzapOSUrl(params);
  if (isEmbeddedPreview()) window.open(url, "_blank", "noopener,noreferrer");
  else window.location.assign(url);
};

function OptionCard({ opt, selected, onClick }) {
  const t = useT();
  const Icon = opt.icon;
  return (
    <button type="button" data-testid={`wattbot-opt-${opt.id}`} onClick={onClick} aria-pressed={selected}
      className={`${CARD} ${selected ? "border-[#00D4FF] bg-[#00D4FF]/10 text-graphite" : "border-slate-200 bg-white hover:border-[#00D4FF]/50 text-slate-700"}`}>
      <span className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${selected ? "bg-[#00D4FF]/20 text-[#0092b0]" : "bg-mist text-slate-500"}`}>
        <Icon size={16} />
      </span>
      <span className="leading-snug">{t(opt.label)}</span>
      {selected && <Check size={15} className="ml-auto shrink-0 text-[#0092b0]" />}
    </button>
  );
}

function QuestionView({ qIdx, answers, ack, onAnswer, onBack, onContinue }) {
  const t = useT();
  const q = QUESTIONS[qIdx];
  const val = answers[q.id] || (q.multi ? [] : null);
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-[#0092b0] font-medium">{t(q.step)}</div>
      <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden" role="progressbar" aria-valuenow={qIdx + 1} aria-valuemin={1} aria-valuemax={QUESTIONS.length}>
        <div className="h-full bg-wz-gradient rounded-full transition-all" style={{ width: `${((qIdx + 1) / QUESTIONS.length) * 100}%` }} />
      </div>
      {ack && <p data-testid="wattbot-ack" className="mt-3 text-xs text-slate-600 bg-mist border border-slate-200 rounded-lg px-3 py-2 leading-relaxed">{t(ack)}</p>}
      <h3 className="mt-4 font-heading text-lg text-graphite">{t(q.ask)}</h3>
      {q.hint && <p className="mt-1 text-xs text-slate-500">{t(q.hint)}</p>}
      <div className="mt-4 grid gap-2">
        {q.options.map((o) => (
          <OptionCard key={o.id} opt={o}
            selected={q.multi ? val.includes(o.id) : val === o.id}
            onClick={() => onAnswer(q, o.id)} />
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between">
        <button type="button" data-testid="wattbot-back" onClick={onBack} disabled={qIdx === 0}
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-graphite disabled:opacity-30 focus-visible:ring-2 focus-visible:ring-[#00D4FF] rounded px-1 focus:outline-none">
          <ArrowLeft size={13} /> {t("Back")}
        </button>
        {q.multi && (
          <button type="button" data-testid="wattbot-continue" onClick={onContinue} disabled={!val.length}
            className="btn-gradient inline-flex items-center gap-2 px-5 py-2 rounded-full text-xs disabled:opacity-40">
            {t("Continue")} <ArrowRight size={13} />
          </button>
        )}
      </div>
    </div>
  );
}

function ResultView({ answers, rec, onStart, onExplore, onConsult, onBrowse }) {
  const t = useT();
  const findLabel = (qid, id) => QUESTIONS.find((q) => q.id === qid)?.options.find((o) => o.id === id)?.label || "—";
  const industry = t(findLabel("industry", answers.industry));
  const bill = t(findLabel("bill", answers.bill));
  const state = t(findLabel("state", answers.state));
  const concerns = (answers.problem || []).map((p) => findLabel("problem", p)).filter((l) => l !== "—").map((l) => t(l));
  const primary = PRODUCT_META[rec.primary];
  return (
    <div data-testid="wattbot-result">
      <h3 className="font-heading text-lg text-graphite">{t("Your WattZap Recommendation")}</h3>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <div className="bg-mist border border-slate-200 rounded-lg px-3 py-2">
          <div className="text-slate-400">{t("Your facility")}</div>
          <div className="mt-0.5 font-medium text-graphite">{industry}</div>
        </div>
        <div className="bg-mist border border-slate-200 rounded-lg px-3 py-2">
          <div className="text-slate-400">{t("Monthly bill")}</div>
          <div className="mt-0.5 font-medium text-graphite">{bill}</div>
        </div>
        <div className="bg-mist border border-slate-200 rounded-lg px-3 py-2">
          <div className="text-slate-400">{t("State / DISCOM")}</div>
          <div className="mt-0.5 font-medium text-graphite leading-snug">{state}</div>
        </div>
        <div className="bg-mist border border-slate-200 rounded-lg px-3 py-2">
          <div className="text-slate-400">{t("To investigate")}</div>
          <div className="mt-0.5 font-medium text-graphite leading-snug">{concerns.join(" · ") || "—"}</div>
        </div>
      </div>
      <div className="mt-3">
        <div className="text-xs font-medium text-graphite">{t("Potential areas to investigate")}</div>
        <p className="text-[11px] text-slate-400">{t("Based on your answers, these may be relevant areas to investigate.")}</p>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {rec.areas.map((a) => <span key={a} className="px-2.5 py-1 rounded-full text-[11px] bg-[#FFC857]/10 border border-[#FFC857]/40 text-slate-700">{t(a)}</span>)}
        </div>
      </div>
      <div className="mt-3 border border-[#21F59B]/40 bg-[#21F59B]/5 rounded-xl px-4 py-3">
        <div className="text-[11px] uppercase tracking-widest text-[#0092b0]">{t("Recommended starting point")}</div>
        <div className="mt-1 font-heading text-base text-graphite">{primary.name}</div>
        <p className="mt-1 text-xs text-slate-600 leading-relaxed">{t(rec.message)}</p>
      </div>
      <div className="mt-3 text-xs text-slate-600">
        <span className="font-medium text-graphite">{t("Recommended solution path: ")}</span>
        {rec.products.map((p, i) => (
          <span key={p}>{i > 0 && <span className="text-slate-400"> → </span>}<span className="text-[#0092b0] font-medium">{PRODUCT_META[p].name}</span></span>
        ))}
      </div>
      <div className="mt-4 grid gap-2">
        <button type="button" data-testid="wattbot-cta-start" onClick={onStart} className="btn-gradient inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-xs">
          {t("Start My Free EnergyScan")} <ExternalLink size={13} />
        </button>
        <p data-testid="wattbot-wakeup-note" className="text-[10px] text-slate-400 text-center leading-relaxed -mt-0.5">
          {t("Opens the secure WattZap EnergyScan portal. The preview service may take up to one minute to start.")}
        </p>
        <button type="button" data-testid="wattbot-cta-explore" onClick={onExplore} className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-xs font-medium border border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540] hover:text-white transition-colors focus-visible:ring-2 focus-visible:ring-[#00D4FF] focus:outline-none">
          {t("Explore My Recommended Solution")} <ArrowRight size={13} />
        </button>
        <button type="button" data-testid="wattbot-cta-consult" onClick={onConsult} className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-xs font-medium border border-slate-300 text-slate-700 hover:border-slate-500 transition-colors focus-visible:ring-2 focus-visible:ring-[#00D4FF] focus:outline-none">
          <CalendarCheck size={13} /> {t("Book a Consultation")}
        </button>
        <button type="button" data-testid="wattbot-cta-browse" onClick={onBrowse} className="text-[11px] text-slate-400 hover:text-slate-600 underline underline-offset-2 focus-visible:ring-2 focus-visible:ring-[#00D4FF] rounded focus:outline-none">
          {t("Continue Browsing the Website")}
        </button>
      </div>
    </div>
  );
}

function RedirectView({ reduced, params }) {
  const t = useT();
  const [secs, setSecs] = useState(6);
  useEffect(() => {
    const iv = setInterval(() => setSecs((s) => Math.max(0, s - 1)), 1000);
    const timer = setTimeout(() => goToOS(params), 6000);
    return () => { clearInterval(iv); clearTimeout(timer); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="py-6 flex flex-col items-center text-center" data-testid="wattbot-redirecting">
      <WattBotCharacter state="happy" size={64} reduced={reduced} />
      <p className="mt-4 text-sm font-medium text-graphite max-w-xs leading-relaxed">{t(OS_REDIRECT_NOTICE)}</p>
      <p className="mt-2 text-xs text-slate-500 inline-flex items-center gap-1.5">
        <Loader2 size={13} className="animate-spin text-[#0092b0]" /> {t("Redirecting in")} {secs}s…
      </p>
      <button type="button" data-testid="wattbot-open-now" onClick={() => goToOS(params)}
        className="mt-4 btn-gradient inline-flex items-center gap-2 px-5 py-2 rounded-full text-xs">
        {t("Open Now")} <ExternalLink size={13} />
      </button>
      <a
        href={buildWattzapOSUrl(params)}
        target={isEmbeddedPreview() ? "_blank" : "_self"}
        rel="noopener noreferrer"
        data-testid="wattbot-os-fallback"
        className="mt-3 text-[11px] text-slate-500 hover:text-[#0092b0] underline underline-offset-2 focus-visible:ring-2 focus-visible:ring-[#00D4FF] rounded focus:outline-none"
      >
        {t(OS_FALLBACK_TEXT)}
      </a>
    </div>
  );
}

export default function WattBot() {
  const t = useT();
  const navigate = useNavigate();
  const reduced = usePrefersReducedMotion();
  const init = load();
  const [visible, setVisible] = useState(!!init.seen);
  const [greetOpen, setGreetOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [phase, setPhase] = useState(init.phase || "quiz");
  const [qIdx, setQIdx] = useState(init.qIdx || 0);
  const [answers, setAnswers] = useState(init.answers || {});
  const [ack, setAck] = useState("");
  const [rec, setRec] = useState(init.rec || null);
  const [guide, setGuide] = useState(null); // { idx }
  const panelRef = useRef(null);

  useEffect(() => {
    if (init.seen) return;
    const t = setTimeout(() => {
      setVisible(true);
      if (!load().dismissed) setGreetOpen(true);
      save({ seen: true });
      track("concierge_displayed");
    }, 5000);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { setPanelOpen(false); setGreetOpen(false); setGuide(null); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const persist = (patch) => save(patch);

  const startGuide = () => { setGreetOpen(false); setPanelOpen(true); track("concierge_started"); };
  const notNow = () => { setGreetOpen(false); persist({ dismissed: true }); track("concierge_declined"); };
  const minimise = () => { setPanelOpen(false); setGuide(null); persist({ phase: phase === "redirect" ? "result" : phase, qIdx, answers, rec }); track("concierge_minimised"); };
  const reopen = () => { setPanelOpen(true); setGreetOpen(false); if (phase === "redirect") setPhase("result"); track("concierge_reopened"); };
  const startAgain = () => { setAnswers({}); setQIdx(0); setPhase("quiz"); setRec(null); setAck(""); setGuide(null); persist({ answers: {}, qIdx: 0, phase: "quiz", rec: null }); };
  const openChat = () => {
    setPanelOpen(false); setGreetOpen(false); setGuide(null);
    persist({ phase, qIdx, answers, rec });
    window.dispatchEvent(new Event("wattzap:open-chat"));
    track("concierge_chat_handoff");
  };

  const finishQuiz = (finalAnswers) => {
    setPhase("analysing");
    const r = recommend(finalAnswers);
    setTimeout(() => {
      setRec(r); setPhase("result");
      persist({ phase: "result", answers: finalAnswers, rec: r });
      track("concierge_assessment_completed");
      track("concierge_product_recommended", { product: r.primary, route: r.routeId });
    }, reduced ? 200 : 1400);
  };

  const onAnswer = (q, optId) => {
    let next;
    if (q.multi) {
      const cur = answers[q.id] || [];
      next = { ...answers, [q.id]: cur.includes(optId) ? cur.filter((x) => x !== optId) : [...cur, optId] };
      setAnswers(next); persist({ answers: next });
      return;
    }
    next = { ...answers, [q.id]: optId };
    setAnswers(next); persist({ answers: next, qIdx: qIdx + 1 });
    track("concierge_question_answered", { question: q.id });
    if (q.id === "industry") setAck(INDUSTRY_ACK[optId] || "");
    else setAck("");
    if (qIdx < QUESTIONS.length - 1) setQIdx(qIdx + 1);
    else finishQuiz(next);
  };
  const onContinue = () => {
    track("concierge_question_answered", { question: QUESTIONS[qIdx].id });
    if (qIdx < QUESTIONS.length - 1) { setQIdx(qIdx + 1); persist({ qIdx: qIdx + 1 }); }
    else finishQuiz(answers);
  };
  const onBack = () => { if (qIdx > 0) { setQIdx(qIdx - 1); setAck(""); persist({ qIdx: qIdx - 1 }); } };

  const startEnergyScan = () => {
    setPhase("redirect");
    track("concierge_energyscan_redirect");
  };

  const explore = (idx = 0) => {
    if (!rec) return;
    const prod = PRODUCT_META[rec.products[idx]];
    setPanelOpen(false);
    setGuide({ idx });
    navigate(prod.to);
    track("concierge_product_page_opened", { product: rec.products[idx] });
    setTimeout(() => {
      const el = document.querySelector('[data-testid="product-title"]');
      if (el) { el.classList.add("wattbot-hl"); setTimeout(() => el.classList.remove("wattbot-hl"), 2600); }
    }, 600);
  };

  const consult = () => {
    setPanelOpen(false); setGuide(null);
    persist({ phase: "result", qIdx, answers, rec });
    navigate("/contact");
    track("concierge_consultation_started");
  };

  if (!visible) return null;

  const charState = phase === "analysing" ? "think" : phase === "result" || phase === "redirect" ? "happy" : greetOpen ? "wave" : "idle";

  return (
    <div data-testid="wattbot-root">
      <style>{`
        .wattbot-hl { outline: 3px solid #00D4FF; outline-offset: 6px; border-radius: 6px; transition: outline-color .4s ease; }
        @keyframes wbPulse { 0% { box-shadow: 0 0 0 0 rgba(0,212,255,.5); } 70% { box-shadow: 0 0 0 14px rgba(0,212,255,0); } 100% { box-shadow: 0 0 0 0 rgba(0,212,255,0); } }
        @keyframes wbIn { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .wb-in { animation: wbIn .5s ease-out both; }
        @media (prefers-reduced-motion: reduce) { .wb-in { animation: none; } }
      `}</style>

      {/* Launcher */}
      {!panelOpen && !greetOpen && !guide && (
        <button
          type="button"
          data-testid="wattbot-launcher"
          onClick={reopen}
          aria-label="Open the WattZap Concierge"
          className="fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full bg-[#0A2540] border border-[#00D4FF]/40 shadow-lg flex items-center justify-center hover:scale-105 transition-transform focus-visible:ring-2 focus-visible:ring-[#00D4FF] focus:outline-none"
          style={reduced ? undefined : { animation: "wbPulse 2.6s ease-out 2" }}
        >
          <WattBotCharacter state="idle" size={34} reduced={reduced} />
        </button>
      )}

      {/* Greeting bubble */}
      {greetOpen && (
        <div className="wb-in fixed bottom-5 right-4 z-40 w-[min(92vw,320px)]" role="dialog" aria-label="WattZap Concierge greeting">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-2xl p-4">
            <div className="flex items-start gap-3">
              <WattBotCharacter state="wave" size={54} reduced={reduced} />
              <div>
                <div className="font-heading text-base text-graphite">{t("Hi, I'm the WattZap Concierge")} ⚡</div>
                <p className="mt-1 text-xs text-slate-600 leading-relaxed">{t("High electricity bills can have many causes. Answer four quick questions and I'll point you to the right WattZap solution — and your free EnergyScan.")}</p>
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <button type="button" data-testid="wattbot-yes" onClick={startGuide} className="btn-gradient flex-1 px-4 py-2 rounded-full text-xs">{t("Yes, Guide Me")}</button>
              <button type="button" data-testid="wattbot-not-now" onClick={notNow} className="flex-1 px-4 py-2 rounded-full text-xs border border-slate-300 text-slate-600 hover:border-slate-400 transition-colors focus-visible:ring-2 focus-visible:ring-[#00D4FF] focus:outline-none">{t("Not Now")}</button>
            </div>
            <button type="button" data-testid="wattbot-open-chat" onClick={openChat} className="mt-2 w-full text-center text-[11px] text-slate-400 hover:text-[#0092b0] underline underline-offset-2 focus-visible:ring-2 focus-visible:ring-[#00D4FF] rounded focus:outline-none">
              {t("or ask the WattZap AI assistant a question")}
            </button>
          </div>
        </div>
      )}

      {/* Guide bubble on product pages */}
      {guide && rec && (
        <div className="wb-in fixed bottom-5 right-4 z-40 w-[min(92vw,320px)]" role="dialog" aria-label="WattZap Concierge navigation guide">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-2xl p-4">
            <div className="flex items-start gap-3">
              <WattBotCharacter state="happy" size={46} reduced={reduced} />
              <p data-testid="wattbot-guide-msg" className="text-xs text-slate-600 leading-relaxed">
                Based on your answers, <strong className="text-graphite">{PRODUCT_META[rec.products[guide.idx]].name}</strong> is the most relevant place to start{guide.idx > 0 ? " next" : ""}.
              </p>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {guide.idx < rec.products.length - 1 && (
                <button type="button" data-testid="wattbot-guide-next" onClick={() => explore(guide.idx + 1)} className="btn-gradient px-4 py-1.5 rounded-full text-[11px]">Next: {PRODUCT_META[rec.products[guide.idx + 1]].name}</button>
              )}
              <button type="button" data-testid="wattbot-guide-results" onClick={() => { setGuide(null); setPanelOpen(true); }} className="px-4 py-1.5 rounded-full text-[11px] border border-slate-300 text-slate-600 focus-visible:ring-2 focus-visible:ring-[#00D4FF] focus:outline-none">Back to results</button>
              <button type="button" onClick={() => setGuide(null)} aria-label="Close guide" className="px-3 py-1.5 rounded-full text-[11px] text-slate-400 hover:text-slate-600 focus-visible:ring-2 focus-visible:ring-[#00D4FF] focus:outline-none">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Panel */}
      {panelOpen && (
        <div ref={panelRef} role="dialog" aria-modal="false" aria-label="WattZap Concierge guided assessment"
          data-testid="wattbot-panel"
          className="wb-in fixed bottom-3 right-3 sm:bottom-5 sm:right-5 z-[60] w-[min(94vw,400px)] max-h-[78vh] bg-white border border-slate-200 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          <div className="flex items-center gap-2.5 px-4 py-3 bg-[#071A2D]">
            <WattBotCharacter state={charState} size={30} reduced={reduced} />
            <div className="text-white text-sm font-heading" data-testid="wattbot-title">{t("WattZap Concierge")}</div>
            <div className="ml-auto flex items-center gap-1">
              <button type="button" onClick={openChat} data-testid="wattbot-header-chat" aria-label="Ask the WattZap AI assistant a question" title="Ask a question (AI chat)" className="p-1.5 text-white/60 hover:text-white focus-visible:ring-2 focus-visible:ring-[#00D4FF] rounded focus:outline-none"><Sparkles size={14} /></button>
              <button type="button" onClick={startAgain} data-testid="wattbot-start-again" aria-label="Start again" title="Start Again" className="p-1.5 text-white/60 hover:text-white focus-visible:ring-2 focus-visible:ring-[#00D4FF] rounded focus:outline-none"><RotateCcw size={14} /></button>
              <button type="button" onClick={minimise} data-testid="wattbot-minimise" aria-label="Minimise the Concierge" className="p-1.5 text-white/60 hover:text-white focus-visible:ring-2 focus-visible:ring-[#00D4FF] rounded focus:outline-none"><ChevronDown size={15} /></button>
              <button type="button" onClick={minimise} data-testid="wattbot-close" aria-label="Close the Concierge" className="p-1.5 text-white/60 hover:text-white focus-visible:ring-2 focus-visible:ring-[#00D4FF] rounded focus:outline-none"><X size={15} /></button>
            </div>
          </div>
          <div className="p-4 overflow-y-auto">
            {phase === "quiz" && <QuestionView qIdx={qIdx} answers={answers} ack={ack} onAnswer={onAnswer} onBack={onBack} onContinue={onContinue} />}
            {phase === "analysing" && (
              <div className="py-8 flex flex-col items-center text-center" data-testid="wattbot-analysing">
                <WattBotCharacter state="think" size={70} reduced={reduced} />
                <p className="mt-3 text-xs text-slate-600">{t("Matching your answers with the most relevant WattZap solution…")}</p>
              </div>
            )}
            {phase === "result" && rec && (
              <ResultView answers={answers} rec={rec}
                onStart={startEnergyScan}
                onExplore={() => explore(0)}
                onConsult={consult}
                onBrowse={minimise} />
            )}
            {phase === "redirect" && <RedirectView reduced={reduced} params={conciergeParams(answers)} />}
          </div>
        </div>
      )}
    </div>
  );
}
